import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, StatusBadge } from "@/components/kit";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Trash2, Search, KeyRound, Copy } from "lucide-react";

const ROLE = { admin: "Administrador", therapist: "Hipnoterapeuta", client: "Cliente" };

export default function AdminUsers() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [resultPw, setResultPw] = useState("");

  const load = () => api.get("/admin/users").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);

  const openReset = (u) => { setResetUser(u); setResetPw(""); setResultPw(""); };
  const doReset = async () => {
    try {
      const { data } = await api.post(`/admin/users/${resetUser.id}/reset-password`, resetPw ? { password: resetPw } : {});
      setResultPw(data.password);
      toast.success("Senha redefinida");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/users/${toDelete.id}`);
      toast.success("Usuário excluído da plataforma");
      setToDelete(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const term = q.trim().toLowerCase();
  const filtered = term ? list.filter((u) => (u.name || "").toLowerCase().includes(term) || (u.email || "").toLowerCase().includes(term)) : list;

  return (
    <div>
      <PageHeader title="Usuários" subtitle="Todos os usuários da plataforma" />

      <div className="relative max-w-md mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome ou e-mail" className="pl-9 rounded-full" data-testid="users-search" />
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead><TableHead>E-mail</TableHead>
              <TableHead>Papel</TableHead><TableHead>Status</TableHead><TableHead>Cadastro</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-muted-foreground">{u.email}</TableCell>
                <TableCell><Badge variant="secondary">{ROLE[u.role]}</Badge></TableCell>
                <TableCell>{u.status ? <StatusBadge status={u.status} /> : "—"}</TableCell>
                <TableCell className="text-muted-foreground">{new Date(u.created_at).toLocaleDateString("pt-BR")}</TableCell>
                <TableCell className="text-right">
                  {u.role !== "admin" && (
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openReset(u)} data-testid={`reset-pw-${u.id}`} title="Redefinir senha"><KeyRound className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" className="text-destructive" onClick={() => setToDelete(u)} data-testid={`delete-user-${u.id}`} title="Excluir usuário"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!resetUser} onOpenChange={(o) => !o && setResetUser(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Redefinir senha</DialogTitle><DialogDescription>Gere uma nova senha temporária e repasse ao usuário.</DialogDescription></DialogHeader>
          {resetUser && !resultPw && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Defina uma nova senha para <b>{resetUser.name}</b> ({resetUser.email}) ou deixe em branco para gerar automaticamente. Você poderá copiá-la e enviar ao usuário.</p>
              <div><Label>Nova senha (opcional)</Label><Input value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="Deixe vazio para gerar automaticamente" data-testid="reset-pw-input" className="mt-1" /></div>
            </div>
          )}
          {resultPw && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Senha redefinida com sucesso. Copie e envie ao usuário:</p>
              <div className="flex items-center gap-2 rounded-xl bg-secondary p-3">
                <code className="flex-1 font-mono text-sm break-all" data-testid="reset-result-pw">{resultPw}</code>
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(resultPw); toast.success("Copiada"); }} data-testid="copy-reset-pw"><Copy className="w-4 h-4" /></Button>
              </div>
              <p className="text-xs text-muted-foreground">Por segurança, esta senha não será exibida novamente. Recomende que o usuário a altere após o acesso.</p>
            </div>
          )}
          <DialogFooter>
            {!resultPw
              ? <><Button variant="ghost" onClick={() => setResetUser(null)}>Cancelar</Button><Button onClick={doReset} data-testid="confirm-reset-pw-btn"><KeyRound className="w-4 h-4 mr-1" />Redefinir senha</Button></>
              : <Button onClick={() => setResetUser(null)} data-testid="reset-done-btn">Concluir</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Excluir usuário</DialogTitle></DialogHeader>
          {toDelete && (
            <p className="text-sm text-muted-foreground">
              Tem certeza que deseja excluir <b>{toDelete.name}</b> ({toDelete.email})? Esta ação é permanente e remove os dados relacionados (consultas, exercícios e avaliações).
            </p>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setToDelete(null)}>Cancelar</Button>
            <Button className="bg-destructive hover:bg-destructive/90 text-white" onClick={confirmDelete} data-testid="confirm-delete-user-btn"><Trash2 className="w-4 h-4 mr-1" />Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
